import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BetaAccessForm from '../components/BetaAccessForm';
import {
  checkAccessStatus,
  submitAccessRequest,
  AccessRequestBody,
  AccessStatus,
} from '../api/access';

type PageState =
  | { type: 'initial' }
  | { type: 'checking' }
  | { type: 'form' }
  | { type: 'submitting' }
  | { type: 'success'; message: string }
  | { type: 'already-allowed' }
  | { type: 'already-pending' }
  | { type: 'error'; message: string };

export default function BetaAccess() {
  const [state, setState] = useState<PageState>({ type: 'initial' });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailFromUrl = searchParams.get('email') || '';

  const handleStatusResult = useCallback((status: AccessStatus) => {
    switch (status) {
      case 'allowed':
        setState({ type: 'already-allowed' });
        // Redirect to signup after 3 seconds
        setTimeout(() => {
          navigate('/signup');
        }, 3000);
        break;
      case 'pending':
        setState({ type: 'already-pending' });
        break;
      case 'none':
        setState({ type: 'form' });
        break;
    }
  }, [navigate]);

  const handleCheckStatus = useCallback(async (email: string) => {
    setState({ type: 'checking' });
    try {
      const { status } = await checkAccessStatus(email);
      handleStatusResult(status);
    } catch (error) {
      setState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to check access status',
      });
    }
  }, [handleStatusResult]);

  useEffect(() => {
    // If email provided in URL, auto-check status
    if (emailFromUrl) {
      handleCheckStatus(emailFromUrl);
    } else {
      setState({ type: 'form' });
    }
  }, [emailFromUrl, handleCheckStatus]);

  const handleSubmit = async (data: AccessRequestBody) => {
    setState({ type: 'submitting' });
    try {
      const response = await submitAccessRequest(data);
      setState({ type: 'success', message: response.message });
    } catch (error) {
      setState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to submit request',
      });
    }
  };

  const handleRetry = () => {
    setState({ type: 'form' });
  };

  const handleGoToSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Request Beta Access
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Join the SmartPrompt beta program
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Checking State */}
          {state.type === 'checking' && (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-sm text-gray-600">Checking access status...</p>
            </div>
          )}

          {/* Form State */}
          {state.type === 'form' && (
            <div>
              <p className="mb-6 text-sm text-gray-600">
                SmartPrompt is currently in beta. Fill out the form below to request access.
              </p>
              <BetaAccessForm
                onSubmit={handleSubmit}
                disabled={false}
                initialEmail={emailFromUrl}
              />
            </div>
          )}

          {/* Submitting State */}
          {state.type === 'submitting' && (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-sm text-gray-600">Submitting your request...</p>
            </div>
          )}

          {/* Success State */}
          {state.type === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Request Submitted!</h3>
              <p className="mt-2 text-sm text-gray-600">{state.message}</p>
              <p className="mt-4 text-sm text-gray-500">
                We'll review your request and send you an email once you're approved.
              </p>
            </div>
          )}

          {/* Already Allowed State */}
          {state.type === 'already-allowed' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">You're Approved!</h3>
              <p className="mt-2 text-sm text-gray-600">
                Your email has been approved for beta access.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Redirecting to signup...
              </p>
              <button
                onClick={handleGoToSignup}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Signup Now
              </button>
            </div>
          )}

          {/* Already Pending State */}
          {state.type === 'already-pending' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Request Pending</h3>
              <p className="mt-2 text-sm text-gray-600">
                Your beta access request is currently being reviewed.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                We'll send you an email once your request has been approved.
              </p>
            </div>
          )}

          {/* Error State */}
          {state.type === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Something Went Wrong</h3>
              <p className="mt-2 text-sm text-red-600">{state.message}</p>
              <button
                onClick={handleRetry}
                className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-500">
          Already have access?{' '}
          <a
            href="/signup"
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Sign up here
          </a>
        </p>
      </div>
    </div>
  );
}
